import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityListApiPropertiesModel, ActivityListItemDto } from '@/services/platform/api/model';
import { cn, useLocaleStore } from '@/lib/core';
import { useClientTranslation } from '@/lib/core/i18n';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { getCurrentActivity, getPastActivity } from '@/services/platform/data/activity';
import { ActivityListCurrent } from './current';
import { ActivityListPast } from './past';

type Tab = 'current' | 'past';

interface TabButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function TabButton({ label, active, onClick }: TabButtonProps) {
  return (
    <button type="button" className="flex flex-col items-center gap-0.5" onClick={onClick}>
      <span
        className={cn(
          'font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px] uppercase',
          active ? 'font-semibold' : 'font-normal'
        )}
      >
        {label}
      </span>
      <span className={cn('h-px w-4', active ? 'bg-jncquoi-neutral-1000' : 'bg-transparent')} />
    </button>
  );
}

interface EmptyStateProps {
  icon?: string | null;
  title?: string | null;
  description?: string | null;
}

function EmptyState({ icon, title, description }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-2 pt-22">
      {icon && (
        <div className="flex size-11 items-center justify-center rounded-[52px] p-2.5">
          <Icon name={icon} width={20} height={20} className="text-jncquoi-neutral-1000" />
        </div>
      )}
      <div className="flex w-full flex-col gap-2 text-center">
        {title && (
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
            {title}
          </p>
        )}
        {description && (
          <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[14px] leading-normal tracking-[0.25px]">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

interface ProfileActivityListProps {
  properties: ActivityListApiPropertiesModel;
  shopPath?: string;
  detailPath?: string;
}

export function ProfileActivityList({ properties, shopPath = '/', detailPath }: ProfileActivityListProps) {
  const locale = useLocaleStore(state => state.locale);
  const navigate = useNavigate();
  const t = useClientTranslation();

  const [tab, setTab] = useState<Tab>('current');
  const [currentItems, setCurrentItems] = useState<ActivityListItemDto[]>([]);
  const [pastItems, setPastItems] = useState<ActivityListItemDto[]>([]);
  const [pastCursor, setPastCursor] = useState<string | undefined>(undefined);
  const [pastHasMore, setPastHasMore] = useState(false);
  const [currentLoading, setCurrentLoading] = useState(true);
  const [pastLoading, setPastLoading] = useState(false);
  const [pastLoadingMore, setPastLoadingMore] = useState(false);
  const [pastFetched, setPastFetched] = useState(false);

  useEffect(() => {
    getCurrentActivity(undefined, locale)
      .then(data => setCurrentItems(data?.items ?? []))
      .finally(() => setCurrentLoading(false));
  }, []);

  function fetchPast(cursor?: string) {
    const isMore = cursor != null;

    if (isMore) {
      setPastLoadingMore(true);
    } else {
      setPastLoading(true);
    }

    getPastActivity(cursor ? { before: cursor } : undefined, locale)
      .then(data => {
        const items = data?.items ?? [];
        setPastItems(prev => (isMore ? [...prev, ...items] : items));
        setPastCursor(data?.nextCursor ?? undefined);
        setPastHasMore(data?.hasMore ?? false);
      })
      .finally(() => {
        if (isMore) {
          setPastLoadingMore(false);
        } else {
          setPastLoading(false);
          setPastFetched(true);
        }
      });
  }

  function handleItemClick(id: string) {
    if (detailPath) navigate(`${detailPath}/${id}`);
  }

  function handleTabChange(next: Tab) {
    setTab(next);
    if (next === 'past' && !pastFetched) fetchPast();
  }

  const currentTitle = properties?.currentOrdersTitle ?? t('App.Labels.ActivityTabCurrent');
  const pastTitle = properties?.pastActivityText ?? t('App.Labels.ActivityTabPast');
  const isCurrentEmpty = !currentLoading && currentItems.length === 0;

  function renderCurrentTab() {
    if (currentLoading) return <SpinLoading wrapperClassName="min-h-[30vh]" />;
    if (currentItems.length === 0) {
      return (
        <EmptyState
          icon={properties?.noOrdersIcon}
          title={properties?.noOrdersTitle ?? t('App.Labels.ActivityNoOrders')}
          description={properties?.noOrdersDescription ?? t('App.Labels.ActivityNoOrdersDescription')}
        />
      );
    }
    return (
      <ActivityListCurrent
        items={currentItems}
        trackMessage={properties?.currentTrackMessage ?? t('App.Labels.ActivityTrackMessage')}
        onItemClick={handleItemClick}
      />
    );
  }

  function renderPastTab() {
    if (pastLoading) return <SpinLoading wrapperClassName="min-h-[30vh]" />;
    if (pastFetched && pastItems.length === 0) {
      return (
        <EmptyState
          icon={properties?.noActivityIcon}
          title={properties?.noActivityText ?? t('App.Labels.ActivityNoActivity')}
          description={properties?.noActivityDescription ?? t('App.Labels.ActivityNoActivityDescription')}
        />
      );
    }
    return (
      <ActivityListPast
        items={pastItems}
        hasMore={pastHasMore}
        loadingMore={pastLoadingMore}
        loadMoreLabel={t('App.Labels.ActivityLoadMore')}
        onLoadMore={() => fetchPast(pastCursor)}
        onItemClick={handleItemClick}
      />
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-((max(1rem,env(safe-area-inset-top)))+60px+((env(safe-area-inset-bottom)+1.5rem)*2)+48px))] flex-1 flex-col gap-8 px-4 pt-6">
      {/* tab bar */}
      <div className="flex gap-4">
        <TabButton
          label={currentTitle}
          active={tab === 'current'}
          onClick={() => handleTabChange('current')}
        />
        <TabButton label={pastTitle} active={tab === 'past'} onClick={() => handleTabChange('past')} />
      </div>

      {/* tab content */}
      <div className="flex flex-1 flex-col overflow-y-auto">
        {tab === 'current' && renderCurrentTab()}
        {tab === 'past' && renderPastTab()}
      </div>

      {/* shop CTA — pinned to bottom, only on current empty state */}
      {tab === 'current' && isCurrentEmpty && (
        <div className="pb-0">
          <Button
            variant="primary-solid"
            palette="dark"
            className="w-full"
            onClick={() => navigate(shopPath)}
          >
            {t('App.Labels.ActivityShopNow')}
          </Button>
        </div>
      )}
    </div>
  );
}
